/**
 * Audio utility for text-to-speech functionality
 * Uses native Capacitor TTS for mobile and Web Speech API for web
 * Android-compatible with proper native TTS implementation
 */

import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { Capacitor } from '@capacitor/core';

// Load voices on initialization (for web)
let voicesLoaded = false;
let speechInitialized = false;

const loadVoices = () => {
  if (!voicesLoaded && 'speechSynthesis' in window) {
    window.speechSynthesis.getVoices();
    voicesLoaded = true;
  }
};

// Initialize speech synthesis (for web only)
const initializeSpeech = () => {
  if (!speechInitialized && 'speechSynthesis' in window) {
    try {
      // Simple initialization - create a dummy utterance to initialize the engine
      const utterance = new SpeechSynthesisUtterance('');
      utterance.volume = 0; // Silent
      window.speechSynthesis.speak(utterance);
      window.speechSynthesis.cancel();
      speechInitialized = true;
      console.log('✅ Speech synthesis initialized (Web)');
    } catch (error) {
      console.error('❌ Error initializing speech:', error);
    }
  }
};

// Check if running on native platform
const isNativePlatform = () => {
  return Capacitor.isNativePlatform();
};

if ('speechSynthesis' in window && !isNativePlatform()) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
  
  // Initialize on first user interaction (for web)
  document.addEventListener('touchstart', initializeSpeech, { once: true });
  document.addEventListener('click', initializeSpeech, { once: true });
}

export const speakText = async (text: string, context: 'question' | 'answer' = 'question'): Promise<void> => {
  console.log('🔊 Speaking text:', text);
  console.log('📱 Is native platform?', isNativePlatform());
  console.log('📝 Context:', context);

  // Skip Bengali text (users are Bengali speakers)
  const hasBengali = /[\u0980-\u09FF]/.test(text);
  if (hasBengali && context === 'answer') {
    console.log('⏭️ Skipping Bengali text (users are Bengali speakers)');
    return;
  }

  // Use native TTS for Android/iOS
  if (isNativePlatform()) {
    console.log('✅ Using NATIVE TTS for:', text);
    try {
      await speakWithNativeTTS(text, context);
      console.log('✅ Native TTS completed successfully');
    } catch (error) {
      console.error('❌ Native TTS error:', error);
      console.error('❌ Error details:', JSON.stringify(error));
    }
    return;
  }
  
  console.log('🌐 Using WEB Speech API for:', text);

  // Web Speech API for web browsers
  if (!('speechSynthesis' in window)) {
    console.warn('⚠️ Speech synthesis not supported');
    return;
  }

  // Initialize if not already done
  if (!speechInitialized) {
    initializeSpeech();
  }

  // Cancel any ongoing speech
  window.speechSynthesis.cancel();

  // Small delay to ensure cancellation completes
  setTimeout(() => {
    if (context === 'answer') {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'it-IT'; // Italian for answers
      configureUtterance(utterance);
      
      // Add error handling
      utterance.onerror = (event) => {
        console.error('❌ Speech synthesis error:', event.error);
      };
      
      utterance.onstart = () => {
        console.log('✅ Speech started (Web)');
      };
      
      utterance.onend = () => {
        console.log('✅ Speech ended (Web)');
      };
      
      try {
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('❌ Error calling speak:', error);
      }
    } else {
      // Questions: Only speak Italian/English parts, skip Bengali
      speakOnlyItalianAndEnglish(text);
    }
  }, 100);
};

/**
 * Speak text using native TTS (for Android/iOS)
 */
const speakWithNativeTTS = async (text: string, context: 'question' | 'answer'): Promise<void> => {
  console.log('🎤 speakWithNativeTTS called');
  console.log('🎤 Text to speak:', text);
  console.log('🎤 Context:', context);
  
  try {
    console.log('🛑 Stopping any ongoing speech...');
    await TextToSpeech.stop();
    console.log('✅ Stopped successfully');

    if (context === 'answer') {
      console.log('🗣️ About to speak with native TTS...');
      console.log('🗣️ Language: it-IT');
      
      // Speak the full text in Italian
      await TextToSpeech.speak({
        text: text,
        lang: 'it-IT',
        rate: 0.95,
        pitch: 1.0,
        volume: 1.0,
        category: 'ambient',
      });
      
      console.log('✅ Native TTS spoke (Italian):', text);
    } else {
      console.log('📝 Speaking question with Italian/English parts only');
      // For questions, only speak Italian/English parts
      await speakOnlyItalianAndEnglishNative(text);
    }
  } catch (error) {
    console.error('❌ Error with native TTS:', error);
    console.error('❌ Error type:', typeof error);
    console.error('❌ Error message:', error instanceof Error ? error.message : String(error));
    console.error('❌ Full error object:', error);
  }
};

/**
 * Speak only Italian/English parts using native TTS
 */
const speakOnlyItalianAndEnglishNative = async (text: string): Promise<void> => {
  // Extract only Italian/English text, skip Bengali
  const parts = text.split(/([a-zA-Z\s\-',.!?]+)/).filter(part => part.trim());
  
  for (const part of parts) {
    const hasBengali = /[\u0980-\u09FF]/.test(part);
    const hasItalianOrEnglish = /[a-zA-Z]/.test(part);
    
    // Only speak Italian/English parts
    if (hasItalianOrEnglish && !hasBengali) {
      try {
        await TextToSpeech.speak({
          text: part,
          lang: 'it-IT',
          rate: 0.95,
          pitch: 1.0,
          volume: 1.0,
          category: 'ambient',
        });
      } catch (error) {
        console.error('❌ Error speaking part:', error);
      }
    }
  }
};

const speakOnlyItalianAndEnglish = (text: string): void => {
  // Extract only Italian/English text, skip Bengali
  const parts = text.split(/([a-zA-Z\s\-',.!?]+)/).filter(part => part.trim());
  let delay = 0;
  
  parts.forEach((part) => {
    const hasBengali = /[\u0980-\u09FF]/.test(part);
    const hasItalianOrEnglish = /[a-zA-Z]/.test(part);
    
    // Only speak Italian/English parts
    if (hasItalianOrEnglish && !hasBengali) {
      setTimeout(() => {
        try {
          const utterance = new SpeechSynthesisUtterance(part);
          utterance.lang = 'it-IT'; // Default to Italian
          configureUtterance(utterance);
          
          // Add error handling
          utterance.onerror = (event) => {
            console.error('Speech synthesis error:', event);
          };
          
          window.speechSynthesis.speak(utterance);
        } catch (error) {
          console.error('Error speaking text:', error);
        }
      }, delay);
      
      delay += Math.max(part.length * 80, 600); // Dynamic delay based on text length
    }
  });
};

const configureUtterance = (utterance: SpeechSynthesisUtterance): void => {
  // Optimize for less robotic sound
  utterance.rate = 0.95; // Slightly slower than default (1.0) - more natural
  utterance.volume = 1.0;
  
  // Only configure for Italian (no Bengali)
  const voices = window.speechSynthesis.getVoices();
  
  // For Android, try to find Italian voices
  // On Android, voices might not have localService property
  let italianVoice = voices.find(v => 
    v.lang.startsWith('it') && 
    (v.localService === true || v.localService === undefined) // Local or any Italian voice
  );
  
  // If no Italian voice found, try any Italian-sounding voice
  if (!italianVoice) {
    italianVoice = voices.find(v => v.lang.includes('it') || v.name.toLowerCase().includes('italian'));
  }
  
  if (italianVoice) {
    utterance.voice = italianVoice;
    console.log('🇮🇹 Using Italian voice:', italianVoice.name);
  }
};

export const stopSpeaking = async (): Promise<void> => {
  if (isNativePlatform()) {
    try {
      await TextToSpeech.stop();
    } catch (error) {
      console.error('❌ Error stopping native TTS:', error);
    }
  } else if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export const isSpeaking = (): boolean => {
  // Note: Native TTS doesn't have a reliable way to check speaking status
  // This works for web only
  if (isNativePlatform()) {
    return false; // Not reliably available for native TTS
  }
  return window.speechSynthesis.speaking;
};
